/**
 * ProposalPreview - Preview HTML em tempo real de uma proposta comercial.
 * Espelha o layout e calculos do PDF gerado pelo backend (cpq.py build_proposal_pdf).
 */

const PERIODO_MESES = { anual: 12, "3anos": 36, "5anos": 60, mensal: 1, unica: 1 };
const PERIODO_LABEL = { anual: "1 Ano", "3anos": "3 Anos", "5anos": "5 Anos", mensal: "Mensal", unica: "Unica vez" };
const COND_LABEL = { pronto: "Pronto pagamento", "30d": "30 dias", "60d": "60 dias", "90d": "90 dias", mensal: "Mensalidade" };
const MET_LABEL = { sepa: "SEPA/Debito directo", mb: "Multibanco", cartao: "Cartao", transferencia: "Transferencia", mbway: "MBWay" };

function eur(v) {
  return new Intl.NumberFormat("pt-PT", { style: "currency", currency: "EUR" }).format(Number(v) || 0);
}

function computeProposal(prop) {
  const globalDiscPct = parseFloat(prop?.desconto_pct_global) || 0;
  let subtotalBruto = 0;
  let totalIvaBruto = 0;

  const itens = (prop?.itens || []).map((it) => {
    const qty = parseFloat(it.quantidade) || 1;
    const pu = parseFloat(it.preco_unitario) || 0;
    const descPct = parseFloat(it.desconto_pct) || 0;
    const ivaPct = parseFloat(it.iva_pct) ?? 23;
    const base = qty * pu * (1 - descPct / 100);
    const ivaVal = base * (ivaPct / 100);
    const total_linha = base + ivaVal;
    const periodo = it.periodo || "anual";
    const meses = PERIODO_MESES[periodo] || 12;
    const precoMensal = meses > 1 && periodo !== "unica" ? base / meses : null;
    const ref = parseFloat(it.preco_mensal_ref) || 0;
    const poupanca = (periodo === "3anos" || periodo === "5anos") && ref > 0
      ? Math.max(0, Math.round(ref * meses - base))
      : 0;
    subtotalBruto += base;
    totalIvaBruto += ivaVal;
    return { ...it, base, total_linha, precoMensal, poupanca };
  });

  const descontoGlobal = subtotalBruto * globalDiscPct / 100;
  const subtotal = subtotalBruto - descontoGlobal;
  const total_iva = totalIvaBruto * (1 - globalDiscPct / 100);
  const total = subtotal + total_iva;
  return { itens, subtotal, total_iva, total, descontoGlobal };
}

export function ProposalPreview({ prop, accounts, numero = "RASCUNHO" }) {
  const account = accounts?.find((a) => a.id === prop?.account_id);
  const { itens, subtotal, total_iva, total, descontoGlobal } = computeProposal(prop);
  const hoje = new Date().toLocaleDateString("pt-PT");
  const validade = prop?.validade_dias || 30;

  return (
    <div className="bg-white border border-slate-200 rounded-lg shadow-sm text-[11px] font-sans overflow-hidden"
      data-testid="proposal-preview" style={{ fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif" }}>

      {/* Header navy */}
      <div className="px-6 py-4" style={{ backgroundColor: "#0F172A" }}>
        <div className="flex items-start justify-between">
          <div>
            <div className="text-white font-bold text-base tracking-wide">COGNISEG</div>
            <div className="text-slate-400 text-[10px] mt-0.5">Proposta Comercial</div>
          </div>
          <div className="text-right">
            <div className="text-white font-bold text-sm">{numero}</div>
            <div className="text-slate-400 text-[10px] mt-0.5">{hoje}</div>
            <div className="text-slate-400 text-[10px]">Validade: {validade} dias</div>
          </div>
        </div>
        {prop?.titulo && (
          <div className="text-slate-300 text-xs mt-3 font-medium truncate">{prop.titulo}</div>
        )}
      </div>

      {/* Client info */}
      {account && (
        <div className="px-6 py-3 bg-slate-50 border-b border-slate-100">
          <div className="text-[9px] font-bold text-slate-400 uppercase tracking-widest mb-1">Cliente</div>
          <div className="font-semibold text-slate-800">{account.nome}</div>
          {account.nif && <div className="text-slate-500 text-[10px]">NIF: {account.nif}</div>}
          {account.email && <div className="text-slate-500 text-[10px]">{account.email}</div>}
        </div>
      )}

      {/* Items table */}
      <div className="px-6 py-3">
        <table className="w-full text-[10px]" data-testid="preview-items-table">
          <thead>
            <tr style={{ backgroundColor: "#0F172A" }}>
              <th className="text-white text-left px-2 py-1.5 font-semibold rounded-tl">Descricao</th>
              <th className="text-white text-right px-2 py-1.5 font-semibold w-8">Qtd</th>
              <th className="text-white text-right px-2 py-1.5 font-semibold w-20">Preco unit.</th>
              <th className="text-white text-right px-2 py-1.5 font-semibold w-10">IVA%</th>
              <th className="text-white text-right px-2 py-1.5 font-semibold w-20 rounded-tr">Total</th>
            </tr>
          </thead>
          <tbody>
            {itens.length === 0 && (
              <tr><td colSpan={5} className="text-center text-slate-400 italic py-4 text-[10px]">Sem linhas ainda</td></tr>
            )}
            {itens.map((it, i) => {
              const periodo = it.periodo || "anual";
              const isPluri = periodo === "3anos" || periodo === "5anos";
              return (
                <tr key={i} style={{ backgroundColor: i % 2 === 0 ? "#ffffff" : "#F8FAFC" }}>
                  <td className="px-2 py-1.5 align-top">
                    <div className="font-medium text-slate-800">{it.descricao || <span className="italic text-slate-400">sem descricao</span>}</div>
                    {isPluri && it.precoMensal != null ? (
                      <div className="text-[9px] font-semibold mt-0.5" style={{ color: "#059669" }}>
                        {PERIODO_LABEL[periodo]} | {eur(it.precoMensal)}/mes
                        {it.poupanca > 0 && <span className="ml-1">| Poupa {eur(it.poupanca)}</span>}
                      </div>
                    ) : (
                      <div className="text-[9px] text-slate-400 mt-0.5">{PERIODO_LABEL[periodo]}</div>
                    )}
                  </td>
                  <td className="px-2 py-1.5 text-right text-slate-700 align-top">{parseFloat(it.quantidade) || 1}</td>
                  <td className="px-2 py-1.5 text-right text-slate-700 align-top">{eur(parseFloat(it.preco_unitario) || 0)}</td>
                  <td className="px-2 py-1.5 text-right text-slate-700 align-top">{parseFloat(it.iva_pct) ?? 23}%</td>
                  <td className="px-2 py-1.5 text-right font-semibold text-slate-800 align-top">{eur(it.total_linha)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Totals */}
      <div className="px-6 pb-4">
        <div className="ml-auto w-48 space-y-1 text-[10px]">
          <div className="flex justify-between text-slate-600">
            <span>Subtotal</span><span>{eur(subtotal)}</span>
          </div>
          {descontoGlobal > 0 && (
            <div className="flex justify-between text-emerald-600 font-semibold">
              <span>Desconto global ({prop?.desconto_pct_global}%)</span>
              <span>-{eur(descontoGlobal)}</span>
            </div>
          )}
          <div className="flex justify-between text-slate-600">
            <span>IVA</span><span>{eur(total_iva)}</span>
          </div>
          <div className="flex justify-between font-bold text-slate-900 border-t border-slate-200 pt-1">
            <span>Total</span><span style={{ color: "#1E40AF" }}>{eur(total)}</span>
          </div>
        </div>
      </div>

      {/* Conditions footer */}
      {(prop?.condicoes_pagamento || prop?.metodo_pagamento || prop?.notas) && (
        <div className="px-6 pb-4 border-t border-slate-100 pt-3 space-y-1">
          {(prop?.condicoes_pagamento || prop?.metodo_pagamento) && (
            <div className="text-[9px] text-slate-500">
              {COND_LABEL[prop?.condicoes_pagamento] || prop?.condicoes_pagamento}
              {prop?.metodo_pagamento && <span className="mx-1">|</span>}
              {MET_LABEL[prop?.metodo_pagamento] || prop?.metodo_pagamento}
            </div>
          )}
          {prop?.notas && (
            <div className="text-[9px] text-slate-500 italic whitespace-pre-line">{prop.notas}</div>
          )}
        </div>
      )}
    </div>
  );
}
